// 🖼️ ArtikelFotosPanel — foto's van de artikels op het document (leverbon/bestelbon), klik = groot bekijken.
import { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, X, Package, CaretLeft, CaretRight } from '@phosphor-icons/react';

export const fotoSrc = (url) => !url ? null : (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

const LS_KEY = 'artikel_fotos_panel_actief';
const EVT = 'artikel-fotos-actief';
const leesActief = () => localStorage.getItem(LS_KEY) === '1'; // standaard uit: de mini-foto's op de lijnen volstaan

// Gedeelde aan/uit-stand (kaart + mini-foto's op de lijnen) — synchroon via window-event
export function useArtikelFotosActief() {
  const [actief, setActief] = useState(leesActief);
  useEffect(() => {
    const h = () => setActief(leesActief());
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  const toggle = () => { localStorage.setItem(LS_KEY, leesActief() ? '0' : '1'); window.dispatchEvent(new Event(EVT)); };
  return [actief, toggle];
}

// Mini-foto vooraan een artikellijn (vaste 28×28 box zodat de lijn netjes uitgelijnd blijft)
export function LijnFoto({ artikel, onOpen }) {
  const src = fotoSrc(artikel?.foto_url);
  if (!src) return <div className="w-7 h-7 rounded-sm border border-dashed border-gray-200 bg-gray-50 mx-auto" title={artikel ? 'Geen foto' : ''} />;
  return (
    <button type="button" onClick={() => onOpen?.({ src, code: artikel.art_code, omschr: artikel.omschrijving })}
      className="block w-7 h-7 rounded-sm border border-gray-200 bg-white overflow-hidden mx-auto hover:border-[#002FA7] hover:shadow cursor-zoom-in"
      title={`${artikel.art_code} — klik om te vergroten`} data-testid={`line-foto-${artikel.art_code}`}>
      <img src={src} alt={artikel.art_code} loading="lazy" className="w-full h-full object-contain" />
    </button>
  );
}

export default function ArtikelFotosPanel({ lijnen = [], artikels = [], compact = false }) {
  const [open, setOpen] = useState(null);
  const [actief, toggle] = useArtikelFotosActief();
  const items = useMemo(() => {
    const gezien = new Map();
    for (const l of lijnen) {
      if (!l.art_id) continue;
      const art = artikels.find(a => a.id === l.art_id);
      if (!art) continue;
      const k = art.id;
      const bestaand = gezien.get(k);
      const aantal = parseFloat(String(l.aantal).replace(',', '.')) || 0;
      if (bestaand) { bestaand.aantal += aantal; continue; }
      gezien.set(k, { id: art.id, code: art.art_code, omschr: l.omschr || art.omschrijving, aantal, src: fotoSrc(art.foto_url), variant: l.variant_label || '' });
    }
    return [...gezien.values()];
  }, [lijnen, artikels]);

  if (items.length === 0) return null;
  const metFoto = items.filter(i => i.src).length;
  const idx = open !== null ? items.findIndex(i => i.id === open) : -1;
  const stap = (d) => { const n = (idx + d + items.length) % items.length; setOpen(items[n].id); };

  return (
    <>
      <div className={`bg-white border border-gray-200 ${compact ? 'mb-3' : ''}`} data-testid="artikel-fotos-panel">
        <div className={`flex items-center justify-between px-3 py-2 ${actief ? 'border-b border-gray-100' : ''}`}>
          <span className="text-[10px] font-bold text-gray-400 inline-flex items-center gap-1.5"><ImageIcon size={12} weight="bold" /> Artikelfoto's{!actief && <span className="font-normal">· {items.length} artikel(s), foto's verborgen</span>}</span>
          <div className="flex items-center gap-2">
            {actief && <span className="text-[10px] text-gray-400">{metFoto}/{items.length} met foto</span>}
            <button type="button" role="switch" aria-checked={actief} onClick={toggle}
              className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-[10px] font-bold transition-colors ${actief ? 'bg-[#002FA7] border-[#002FA7] text-white' : 'bg-gray-100 border-gray-300 text-gray-500'}`}
              title={actief ? 'Fotokaart verbergen (mini-foto\'s op de lijnen blijven)' : 'Fotokaart tonen'} data-testid="artikel-fotos-toggle">
              <span className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${actief ? '' : 'translate-x-0'}`} style={{ order: actief ? 2 : 0 }} />
              {actief ? 'Actief' : 'Niet actief'}
            </button>
          </div>
        </div>
        {actief && <div className={`p-3 grid gap-2 ${compact ? 'grid-cols-[repeat(auto-fill,minmax(96px,1fr))]' : 'grid-cols-3'}`}>
          {items.map(it => (
            <button key={it.id} type="button" onClick={() => it.src && setOpen(it.id)} disabled={!it.src}
              className={`group text-left border border-gray-200 rounded-md overflow-hidden bg-gray-50 ${it.src ? 'hover:border-[#002FA7] hover:shadow-md cursor-zoom-in' : 'cursor-default'} transition-all`}
              title={it.src ? `${it.code} — klik om te vergroten` : `${it.code} — geen foto`} data-testid={`artikel-foto-${it.code}`}>
              <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                {it.src
                  ? <img src={it.src} alt={it.code} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                  : <Package size={28} className="text-gray-300" />}
              </div>
              <div className="px-1.5 py-1 border-t border-gray-200">
                <div className="text-[10px] font-mono font-bold text-gray-800 truncate">{it.code}</div>
                <div className="text-[9px] text-gray-500 truncate">{it.aantal ? `${String(it.aantal).replace('.', ',')} × ` : ''}{it.omschr}</div>
              </div>
            </button>
          ))}
        </div>}
      </div>

      {idx >= 0 && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="artikel-foto-lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={() => setOpen(null)} data-testid="artikel-foto-lightbox-close"><X size={28} /></button>
          {items.length > 1 && (<>
            <button className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" onClick={e => { e.stopPropagation(); stap(-1); }}><CaretLeft size={36} /></button>
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" onClick={e => { e.stopPropagation(); stap(1); }}><CaretRight size={36} /></button>
          </>)}
          <div className="max-w-4xl w-full flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
            <img src={items[idx].src} alt={items[idx].code} className="max-h-[75vh] max-w-full object-contain rounded shadow-2xl bg-white" />
            <div className="text-white text-center">
              <div className="font-mono font-bold text-lg">{items[idx].code}</div>
              <div className="text-sm text-white/80">{items[idx].omschr}{items[idx].aantal ? ` · ${String(items[idx].aantal).replace('.', ',')} stuks op dit document` : ''}</div>
              {items.length > 1 && <div className="text-[11px] text-white/50 mt-1">{idx + 1} / {items.length}</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
